/* eslint-disable @typescript-eslint/no-explicit-any */
import { IPaymentMethod, IAsset, ICurrency } from "@/interfaces";
import { IAmlLevel, IDocuments } from "@/interfaces/common";
import gql from "@/repo/apolloClient";
import RestAPIClient from "@/repo/restApi";
import { makeAutoObservable, runInAction } from "mobx";

export enum UI_ACTION_STATE {
  TRANSACTION_PENDING = "TRANSACTION_PENDING",
  TRANSACTION_SUCCESS = "TRANSACTION_SUCCESS",
  TRANSACTION_FAILED = "TRANSACTION_FAILED",
  WALLET_CONFIRMATION_PENDING = "WALLET_CONFIRMATION_PENDING",
  WALLET_CONFIRMATION_SUCCESS = "WALLET_CONFIRMATION_SUCCESS",
  WALLET_CONFIRMATION_FAILED = "WALLET_CONFIRMATION_FAILED",
  PLEASE_WAIT = "PLEASE_WAIT",
  NONE = "NONE",
}
interface CommonState {
  paymentMethods: IPaymentMethod[];
  assets: IAsset[];
  currencies: ICurrency[];
  documents: IDocuments[];
  amlLevels: IAmlLevel[];
  TransactionProcessing: boolean;
  actionState: UI_ACTION_STATE;
}
export default class CommonStore {
  private state: CommonState = {
    paymentMethods: [],
    assets: [],
    currencies: [],
    documents: [],
    amlLevels: [],
    TransactionProcessing: false,
    actionState: UI_ACTION_STATE.NONE,
  };
  constructor() {
    makeAutoObservable(this);
  }
  //#region actions
  public fetchAssets = async (): Promise<Array<IAsset>> => {
    const query = `
      query getAssets {
        assets {
          asset_id
          name
          inactive
          address
          symbol
        }
      }`;
    const result = await gql.Query(query);
    runInAction(() => {
      this.state.assets = (result.data.assets as Array<IAsset>) ?? [];
    });
    return this.state.assets;
  };
  public fetchCurrencies = async (): Promise<Array<IAsset>> => {
    const query = `
      query getCurrencies {
        currency {
          id
          symbol
        }
      }`;
    const result = await gql.Query(query);
    runInAction(() => {
      this.state.currencies = (result.data.currency as Array<ICurrency>) ?? [];
    });
    return this.state.assets;
  };
  public fetchPaymentMethods = async (): Promise<Array<IPaymentMethod>> => {
    const query = `
      query getPaymentMethods {
        payment_methods {
          payment_id
          payment_name
          payment_icon_url
          status
          attributes {
            id
            attribute_name
            attribute_label
            payment_method_id
            is_image
            required
            preview_order
          }
        }
      }`;
    const result = await gql.Query(query);

    runInAction(() => {
      this.state.paymentMethods = result.data
        .payment_methods as Array<IPaymentMethod>;
    });
    return this.state.paymentMethods;
  };
  public fetchDocuments = async (): Promise<void> => {
    const query = `
      query getDocuments {
        documents {
          id
          name
          aml_level_id
        }
      }`;
    const result = await gql.Query(query);
    if (result.data.documents) {
      runInAction(() => {
        this.state.documents = result.data.documents as Array<IDocuments>;
      });
    } else {
      throw new Error("Failed to fetch documents");
    }
  };
  public fetchAmlLevels = async (): Promise<void> => {
    const query = `
      query getAmlLevels {
        aml_level {  
          id
          name
          level
          description
        }
      }`;
    const result = await gql.Query(query);
    if (result.data.aml_level) {
      runInAction(() => {
        this.state.amlLevels = result.data.aml_level as Array<IAmlLevel>;
      });
    } else {
      throw new Error("Failed to fetch aml levels");
    }
  };
  public setActionState = (value: UI_ACTION_STATE): void => {
    runInAction(() => {
      this.state.actionState = value;
    });
  };
  public healthCheck = async (): Promise<boolean> => {
    const client = new RestAPIClient("healthcheck");
    await client.healthCheck();
    return true;
  };
  public setTransactionProcessing = (value: boolean): void => {
    runInAction(() => {
      this.state.TransactionProcessing = value;
    });
  };

  //#endregion
  //#region getters
  public get paymentMethods() {
    return this.state.paymentMethods;
  }
  public get assets() {
    return this.state.assets;
  }
  public get currencies() {
    return this.state.currencies;
  }
  public get documents() {
    return this.state.documents;
  }
  public get amlLevels() {
    return this.state.amlLevels;
  }
  public get TransactionProcessing() {
    return this.state.TransactionProcessing;
  }
  public get actionState() {
    return this.state.actionState;
  }
  //#endregion
}
